// ── Icon lookup map ───────────────────────────────────────────────────────────
import {
  FileText,
  CalendarClock,
  UserCog,
  Briefcase,
  User,
  Link,
  MapPin,
  GraduationCap,
  Building2,
  FileSearch,
  ShieldAlert,
  Building,
  GitCompare,
  Globe,
  Ruler,
  Text,
  Factory,
  ScrollText,
  Package,
  Code,
  Layers,
  BookOpen,
  Award,
  Server,
  Map as MapIcon,
  Monitor,
  OctagonX,
  Share2,
  Cpu,
  CircleAlert,
  Radio,
  CircleCheck,
  File,
  Shield,
  Hash,
  AtSign,
  Lock,
  TriangleAlert,
  Calendar,
  DollarSign,
  Search,
  Zap,
  Flag,
  ChartBar,
  Star,
  Mail,
  Wifi,
  HardDrive,
  UserCheck,
  CircleHelp,
  ExternalLink,
  Tag,
  Activity,
  Info,
  Fingerprint,
  Satellite,
  ShieldCheck,
  Clock,
  Library,
  Crosshair,
  Flame,
  Swords,
  BookMarked,
  PieChart,
  Landmark,
  Target,
  Skull,
  Wrench,
  SlidersHorizontal,
} from "lucide-react";
import { icons as lucideIcons } from "lucide-react";

const ICON_MAP = {
  SOFTWARE: Code,
  CMS: Layers,
  LEDGER: BookOpen,
  LOCATION: MapPin,
  CERTIFICATE: Award,
  WEBHOST: Server,
  STATE: MapIcon,
  OS: Monitor,
  BLACKLIST: OctagonX,
  DESCRIPTION: Text,
  ASN: Share2,
  SERVER_SERVICE: Cpu,
  PERSON: User,
  CVE: CircleAlert,
  CAMPAIGN: Radio,
  TITLE: CircleCheck,
  COURT_FILING: File,
  DOMAIN_CERTIFICATE: Shield,
  SERIAL_NUMBER: Hash,
  SOCIAL_MEDIA: AtSign,
  WEBSITE: Globe,
  CRYPTO: DollarSign,
  JARM: Lock,
  SEVERITY: TriangleAlert,
  CITY: Building2,
  DATE: Calendar,
  DATETIME: CalendarClock,
  CURRENCY: DollarSign,
  DOMAIN_SCANNED: Search,
  ACTION: Zap,
  COUNTRY: Flag,
  RANK: ChartBar,
  SCORE: Star,
  POSTCODE: Mail,
  INDUSTRY: Factory,
  IP_ADDRESS: Wifi,
  SERVER: HardDrive,
  OCCUPATION: Briefcase,
  COMPANY: Building,
  EMPLOYEE: UserCog,
  OWNER: UserCheck,
  REASON: CircleHelp,
  INFO: Info,
  DOCKET_NUMBER: Hash,
  REFERENCE_LINK: ExternalLink,
  NAME: Tag,
  STATUS: Activity,
  SIMILAR: GitCompare,
  URL: Link,
  DOCUMENT: FileText,
  SCHOOL: GraduationCap,
  WORKED_AT: Building2,
  SANCTION_LOOKUP_NAME: FileSearch,
  SANCTION_ENTITY: ShieldAlert,
  SIZE: Ruler,
  BILL_OF_LADING: ScrollText,
  BoL: ScrollText,
  CONTAINER: Package,
  ALIAS: Fingerprint,
  ALIENVAULT_OTX: Satellite,
  COUNTER_OPERATION: ShieldCheck,
  FIRST_SEEN_DATE: Clock,
  INFORMATION_SOURCE: Library,
  MITRE_ATTACK: Crosshair,
  MOTIVATION: Flame,
  OPERATION: Swords,
  PLAYBOOK: BookMarked,
  SECTOR: PieChart,
  SPONSOR: Landmark,
  TARGET_COUNTRY: Target,
  THREAT_ACTOR: Skull,
  TOOL: Wrench,
};

// Validate all icons at load time — log any that are invalid
const FALLBACK_ICON = GitCompare;
Object.entries(ICON_MAP).forEach(([key, val]) => {
  if (!val || (typeof val !== "function" && !val.$$typeof)) {
    console.warn(
      `[MainGraph] ICON_MAP["${key}"] is not a valid component:`,
      val,
    );
    ICON_MAP[key] = FALLBACK_ICON;
  }
});

const safeIcon = (key) => {
  const icon = ICON_MAP[key];
  if (icon && (typeof icon === "function" || icon.$$typeof)) return icon;
  return FALLBACK_ICON;
};

const NODE_TYPE_OPTIONS = Object.keys(ICON_MAP);

/* ─── Dynamic icon lookup from full lucide registry (for custom view icons) ─── */
const pascalToKebab = (str) =>
  str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

const LUCIDE_ICON_ENTRIES = Object.entries(lucideIcons)
  .filter(([name]) => !/^\d/.test(name) && name.length > 1)
  .map(([name, component]) => ({
    key: pascalToKebab(name),
    name,
    component,
  }))
  .sort((a, b) => a.key.localeCompare(b.key));

const LUCIDE_ICON_MAP = {};
LUCIDE_ICON_ENTRIES.forEach((e) => {
  LUCIDE_ICON_MAP[e.key] = e.component;
});

const graphViewIcon = (key) =>
  LUCIDE_ICON_MAP[key] || lucideIcons.SlidersHorizontal;

const POPULAR_ICON_KEYS = new Set([
  "globe",
  "earth",
  "radar",
  "shield",
  "shield-alert",
  "shield-check",
  "sliders-horizontal",
  "crosshair",
  "newspaper",
  "file-text",
  "bookmark",
  "rss",
  "search",
  "star",
  "heart",
  "bell",
  "eye",
  "lock",
  "unlock",
  "zap",
  "flame",
  "target",
  "flag",
  "tag",
  "hash",
  "at-sign",
  "activity",
  "trending-up",
  "bar-chart-3",
  "pie-chart",
  "cpu",
  "server",
  "database",
  "hard-drive",
  "wifi",
  "cloud",
  "folder",
  "file",
  "code",
  "terminal",
  "bug",
  "skull",
  "alert-triangle",
  "alert-circle",
  "info",
  "check-circle",
  "user",
  "users",
  "building",
  "building-2",
  "briefcase",
  "calendar",
  "clock",
  "map-pin",
  "compass",
  "navigation",
  "send",
  "mail",
  "link",
  "external-link",
  "download",
  "upload",
  "share-2",
  "layers",
  "grid",
  "list",
  "layout",
  "layout-dashboard",
  "monitor",
  "smartphone",
  "key",
  "fingerprint",
  "scan",
  "waypoints",
  "git-compare",
  "link-2",
  "swords",
]);

export {
  ICON_MAP,
  FALLBACK_ICON,
  safeIcon,
  NODE_TYPE_OPTIONS,
  LUCIDE_ICON_ENTRIES,
  LUCIDE_ICON_MAP,
  POPULAR_ICON_KEYS,
  graphViewIcon,
};
